<template>
  <div id="JtCardGaugeChart">
    <jt-card-default :cardTitle="cardTitle" :options="cardSelectOptions" :shouldShowSelect="shouldShowCardSelect" @change="onChange">
      <div v-if="isLoading" class="div-loader">
        <div class="spinning-loader rotate"></div>
      </div>
      <div id="content" v-else>
        <div id="topBar">
          <div
            class="legend"
            v-for="(singleData, idx) in computedData"
            :key="`topBarLegend-${idx}`"
          >
            <span>{{ singleData.label }}</span>
            <span class="value" :style="{ color: singleData.color }">
              {{ formattValue(singleData.value) }}
            </span>
          </div>
        </div>
        <hr class="horizontal" />
        <jt-gauge-chart
          class="gauge"
          :data="computedData"
          :fontSize="fontSize"
          :textFormattingType="computedTextFormattingType"
          :targetValue="computedTargetValue"
        />
        <div id="legends">
          <div class="legend">
            <div class="divDot">
              <span
                class="dot"
                :style="{ backgroundColor: gaugeCircleBackGroundColor }"
              />
            </div>
            <div class="informations">
              <span class="label">{{ this.$gettext("PROGRAMMED_TOTAL") }}</span>
              <span>{{ formattValue(computedTargetValue) }}</span>
            </div>
          </div>
          <div
            class="legend"
            v-for="(singleData, idx) in computedData"
            :key="`legend-${idx}`"
          >
            <div class="divDot">
              <span
                class="dot"
                :style="{ backgroundColor: singleData.color }"
              />
            </div>
            <div class="informations">
              <span class="label">{{ singleData.label }}</span>
              <span>{{ formattValue(singleData.value) }}</span>
            </div>
          </div>
        </div>
        <hr class="horizontal" />
        <q-btn v-if="shouldShowBottomButton" flat @click="onClickBottomButton">{{ computedBottomButtonLabel }}</q-btn>
      </div>
    </jt-card-default>
  </div>
</template>

<script>
import JtGaugeChart from 'src/components/charts/JtGaugeChart.vue'
import { jtChartMethods } from 'src/components/charts/shared'
import { dashboardMethods, TEXT_FORMATTING_TYPE } from 'src/components/dashboard/shared'
import { repository } from 'src/jtech/jtech-repository'
import { Component, Vue } from 'vue-property-decorator'
import JtCardDefault from '../../base/JtCardDefault.vue'

@Component({
  name: 'JtCardGaugeChart',
  components: {
    JtCardDefault,
    JtGaugeChart
  },
  props: {
    cardKey: {
      type: [Number, String],
      required: true
    },
    cardTitle: {
      type: String,
      required: true
    },
    cardSelectOptions: {
      type: Array,
      required: false,
      default: []
    },
    data: {
      type: Array,
      required: false,
      default: []
    },
    decimalPlaces: {
      type: Number,
      required: false
    },
    fontSize: {
      type: Number,
      required: false,
      default: 30
    },
    nmInterfaceForData: {
      type: String,
      required: false,
      default: ''
    },
    nmInterfaceGridForData: {
      type: String,
      required: false,
      default: ''
    },
    nmInterfaceForTargetValue: {
      type: String,
      required: false,
      default: ''
    },
    nmInterfaceGridForTargetValue: {
      type: String,
      required: false,
      default: ''
    },
    shouldShowBottomButton: {
      type: Boolean,
      required: false,
      default: true
    },
    bottomButtonLabel: {
      type: String,
      required: false,
      default: ''
    },
    shouldShowCardSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    shouldShowMiddleInfoInPercentage: {
      type: Boolean,
      required: false,
      default: false
    },
    targetValue: {
      type: Number,
      required: true
    },
    textFormattingType: {
      type: Number,
      required: false,
      default: TEXT_FORMATTING_TYPE.DEFAULT
    }
  }
})
export default class JtCardGaugeChart extends Vue {
  isLoading = false
  loadedData = []
  loadedTargetValue = 0

  created () {
    this.loadInformation()
  }

  get computedBottomButtonLabel () {
    return this.bottomButtonLabel || this.$gettext('ACTIVATE_ACTION')
  }

  get computedData () {
    if (this.data && this.data.length > 0) {
      return this.formatData(this.data)
    } else if (this.loadedData && this.loadedData.length > 0) {
      return this.loadedData
    }
    return []
  }

  get computedTargetValue () {
    if (this.targetValue || this.targetValue === 0) return this.targetValue
    if (this.loadedTargetValue || this.loadedTargetValue === 0) return this.loadedTargetValue
    return 0
  }

  get gaugeCircleBackGroundColor () {
    return this.computedData.length === 1 &&
      this.computedData[0]?.color &&
      this.computedData[0].color.startsWith('#') ? `${this.computedData[0].color}20` : '#E6EBF8'
  }

  get computedTextFormattingType () {
    return this.shouldShowMiddleInfoInPercentage ? TEXT_FORMATTING_TYPE.PERCENT : this.textFormattingType
  }

  formattValue (value) {
    const textFormattingType = [TEXT_FORMATTING_TYPE.DATE, TEXT_FORMATTING_TYPE.PERCENT].includes(this.textFormattingType) ? TEXT_FORMATTING_TYPE.DEFAULT : this.textFormattingType
    return dashboardMethods.getTextFormat(value, this.decimalPlaces, textFormattingType, this.$language.current)
  }

  formatData (data) {
    let idxDefaultChartColor = 0
    return data.map((singleData, idx) => {
      singleData.label = singleData.label ||
        (singleData.labelConst ? this.$gettext(singleData.labelConst) : `${this.$gettext('NO_LABEL')}-${idx}`)
      singleData.value = singleData.value || 0
      if (singleData.color) {
        singleData.color = dashboardMethods.convertColorNameToHex(singleData.color)
      } else {
        singleData.color = jtChartMethods.getChartColor()[idxDefaultChartColor]
        idxDefaultChartColor++
        if (idxDefaultChartColor === jtChartMethods.getChartColor().length) {
          idxDefaultChartColor = 0
        }
      }
      return singleData
    })
  }

  async loadInformation (params = {}) {
    this.isLoading = true
    try {
      if (this.nmInterfaceForTargetValue && this.nmInterfaceGridForTargetValue) {
        const { registros: targetRecords } = await repository.resolveDatatableModel(this.nmInterfaceForTargetValue, { ...params, grid: this.nmInterfaceGridForTargetValue })
        if (targetRecords && targetRecords.length > 0) {
          this.loadedTargetValue = targetRecords[0].amount || 0
        } else {
          this.loadedTargetValue = 0
        }
      }
      if (this.nmInterfaceForData && this.nmInterfaceGridForData) {
        const { registros: dataRecords } = await repository.resolveDatatableModel(this.nmInterfaceForData, { ...params, grid: this.nmInterfaceGridForData })
        if (dataRecords && dataRecords.length > 0) {
          this.loadedData = this.formatData(dataRecords)
        } else {
          this.loadedData = []
        }
      }
    } catch (e) {
      console.error(`ChartCard: ${this.cardKey}. There was an error loading data from server.`, e)
    }
    this.isLoading = false
  }

  onChange (newValue) {
    this.loadInformation({ selectedChartOption: newValue })
  }

  onClickBottomButton () {
    this.$emit('click-bottom-button')
  }
}
</script>

<style lang='stylus' scoped>
@import '~quasar-variables';

#JtCardGaugeChart {
  width: 100%;
  height: 100%;

  #content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    #topBar {
      display: flex;
      flex-direction: column;
      margin-top: 5px;
      height: auto;

      .legend {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 30px;

        .value {
          font-weight: 500;
        }
      }
    }

    .horizontal {
      width: 100%;
    }

    .gauge {
      min-height: 150px;
    }

    #legends {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      .legend {
        display: flex;

        .divDot {
          margin-right: 5px;
        }

        .informations {
          display: flex;
          flex-direction: column;

          .label {
            color: black;
          }
        }
      }
    }
  }

  span {
    font-size: 14px;
    font-weight: 300;
  }

  .dot {
    height: 10px;
    width: 10px;
    background-color: #bbb;
    border-radius: 50%;
    display: inline-block;
  }
}
</style>
